// Reserved registration numbers: reserve a batch or daily (Mon–Fri), see reserved numbers, pick one for a new document.
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import type { DocType } from '../types';
import { EmptyState, Field, Modal, Switch, fmtDate, fmtDateTime, todayIso, useToast } from '../ui';
import { Icon } from '../ui/Icon';

export interface Reservation {
  id: number; typeId: number; number: number; year: number; full: string; reservedFor: string; reservedAt: string;
  reservedByName?: string | null; source: 'MANUAL' | 'DAILY'; usedAt?: string | null; usedByName?: string | null; usedByDocument?: number | null;
}
export interface NumberPreview { number: number; year: number; full: string; freeReserved: number }

const DOW = ['duminică', 'luni', 'marți', 'miercuri', 'joi', 'vineri', 'sâmbătă'];
const dayLabel = (iso: string) => { const d = new Date(iso + 'T00:00:00'); return `${DOW[d.getDay()]}, ${fmtDate(iso)}`; };
const fixTs = (iso?: string | null) => (iso ? iso.replace(/([+-]\d{2})$/, '$1:00') : iso);

function groupByDay(list: Reservation[]) {
  const out: { day: string; items: Reservation[] }[] = [];
  for (const r of list) {
    let g = out.find(x => x.day === r.reservedFor);
    if (!g) { g = { day: r.reservedFor, items: [] }; out.push(g); }
    g.items.push(r);
  }
  return out;
}

/** "Rezervă numere" pop-up: tab 1 reserve now / daily schedule, tab 2 list of reserved numbers. */
export function ReservationsModal({ type, onClose, onChanged }: { type: DocType; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const [tab, setTab] = useState<'reserve' | 'list'>('reserve');
  const [count, setCount] = useState(5);
  const [preview, setPreview] = useState<NumberPreview | null>(null);
  const [daily, setDaily] = useState(!!type.dailyReserveEnabled);
  const [dailyCount, setDailyCount] = useState(type.dailyReserveCount || 5);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<'free' | 'used' | 'all'>('free');
  const [list, setList] = useState<Reservation[] | null>(null);

  const loadPreview = () => api.get<NumberPreview>(`/api/catalog/types/${type.id}/number-preview`).then(setPreview).catch(() => {});
  const loadList = () => api.get<Reservation[]>(`/api/catalog/types/${type.id}/reservations?status=${filter}`).then(setList).catch(e => toast(e.message, 'error'));
  useEffect(() => { loadPreview(); }, []);
  useEffect(() => { if (tab === 'list') { setList(null); loadList(); } }, [tab, filter]);

  const n = Math.max(1, Math.min(500, Number(count) || 1));
  const prefix = type.registryPrefix || '';
  const range = preview ? (n === 1 ? preview.full : `${prefix}${preview.number}/${preview.year} – ${prefix}${preview.number + n - 1}/${preview.year}`) : '';

  const reserve = async () => {
    setBusy(true);
    try {
      const r = await api.post<Reservation[]>(`/api/catalog/types/${type.id}/reservations`, { count: n, date: todayIso() });
      toast(`Rezervate ${r.length} numere: ${r[0].full}${r.length > 1 ? ' – ' + r[r.length - 1].full : ''}`, 'ok');
      onChanged(); loadPreview(); setFilter('free'); setTab('list');
    } catch (e: any) { toast(e.message, 'error'); } finally { setBusy(false); }
  };
  const saveDaily = async () => {
    setBusy(true);
    try {
      await api.put(`/api/catalog/types/${type.id}/daily-reservation`, { enabled: daily, count: Math.max(1, Number(dailyCount) || 1) });
      toast(daily ? `Rezervare zilnică activă: ${dailyCount} numere în fiecare zi lucrătoare` : 'Rezervarea zilnică a fost oprită', 'ok');
      onChanged(); loadPreview();
    } catch (e: any) { toast(e.message, 'error'); } finally { setBusy(false); }
  };

  const groups = useMemo(() => groupByDay(list || []), [list]);

  return (
    <Modal title={`Numere rezervate — ${type.name}`} icon="clipboard" onClose={onClose} size="lg" footer={<><span className="grow" /><button className="btn" onClick={onClose}>Închide</button></>}>
      <div className="tabs">
        <button className={tab === 'reserve' ? 'active' : ''} onClick={() => setTab('reserve')}>Rezervă numere</button>
        <button className={tab === 'list' ? 'active' : ''} onClick={() => setTab('list')}>Numere rezervate{preview ? ` (${preview.freeReserved} libere)` : ''}</button>
      </div>

      {tab === 'reserve' && (
        <div className="stack" style={{ gap: 20 }}>
          <div className="card"><div className="card-body stack">
            <h3>Rezervare acum</h3>
            <p className="muted small">Se rezervă numerele următoare din registru, cu data de astăzi ({fmtDate(todayIso())}). Ele nu vor mai fi date automat altor documente.</p>
            <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Field label="Câte numere"><input className="input" type="number" min={1} max={500} value={count} onChange={e => setCount(Number(e.target.value))} style={{ width: 120 }} onKeyDown={e => { if (e.key === 'Enter') reserve(); }} /></Field>
              <div className="field grow"><label>Se vor rezerva</label><div className="res-range">{range || '…'}</div></div>
              <button className="btn primary" onClick={reserve} disabled={busy}><Icon name="check" />Rezervă {n} {n === 1 ? 'număr' : 'numere'}</button>
            </div>
          </div></div>

          <div className="card"><div className="card-body stack">
            <h3>Rezervare zilnică (luni–vineri)</h3>
            <p className="muted small">În fiecare zi lucrătoare sistemul rezervă automat numerele, la prima verificare a zilei (la câteva minute după pornire, apoi la fiecare 5 minute). Dacă activați acum, într-o zi lucrătoare, se rezervă imediat și numerele de astăzi.</p>
            <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Field label=" "><Switch on={daily} onChange={setDaily} label="Rezervă zilnic" /></Field>
              <Field label="Numere pe zi"><input className="input" type="number" min={1} max={200} value={dailyCount} disabled={!daily} onChange={e => setDailyCount(Number(e.target.value))} style={{ width: 120 }} /></Field>
              <span className="grow" />
              <button className="btn" onClick={saveDaily} disabled={busy}><Icon name="clock" />Salvează programarea</button>
            </div>
          </div></div>
        </div>
      )}

      {tab === 'list' && (
        <div className="stack">
          <div className="toolbar">
            <div className="segmented">
              {(['free', 'used', 'all'] as const).map(k => <button key={k} className={filter === k ? 'active' : ''} onClick={() => setFilter(k)}>{k === 'free' ? 'Libere' : k === 'used' ? 'Folosite' : 'Toate'}</button>)}
            </div>
          </div>
          {list == null ? <p className="muted">Se încarcă…</p> : list.length === 0 ? (
            <EmptyState icon="clipboard" title={filter === 'free' ? 'Niciun număr rezervat liber' : 'Niciun număr rezervat'} text="Rezervați numere din primul tab." action={<button className="btn" onClick={() => setTab('reserve')}>Rezervă numere</button>} />
          ) : (
            <div className="table-wrap" style={{ maxHeight: '55vh' }}>
              <table className="table">
                <thead><tr><th>Număr</th><th>Ziua</th><th>Sursa</th><th>Rezervat de</th><th>Stare</th></tr></thead>
                <tbody>
                  {groups.map(g => (
                    <React.Fragment key={g.day}>
                      <tr className="res-day"><td colSpan={5}><Icon name="calendar" size={14} /> {dayLabel(g.day)} <span className="muted">· {g.items.length} {g.items.length === 1 ? 'număr' : 'numere'}, {g.items.filter(r => !r.usedAt).length} libere</span></td></tr>
                      {g.items.map(r => (
                        <tr key={r.id} style={{ cursor: 'default' }}>
                          <td><span className="stamp">{r.full}</span></td>
                          <td className="num">{fmtDate(r.reservedFor)}</td>
                          <td>{r.source === 'DAILY' ? <span className="tag">zilnic</span> : <span className="tag">manual</span>}</td>
                          <td className="muted">{r.reservedByName || '—'} <span className="small">{fmtDateTime(fixTs(r.reservedAt))}</span></td>
                          <td>{r.usedAt ? <span className="chip RESOLVED">Folosit · {r.usedByName}</span> : <span className="chip RECEIVED">Liber</span>}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/** Pick one free reserved number for the document being registered. */
export function ReservedPicker({ type, onPick, onClose }: { type: DocType; onPick: (r: Reservation) => void; onClose: () => void }) {
  const toast = useToast();
  const [list, setList] = useState<Reservation[] | null>(null);
  useEffect(() => { api.get<Reservation[]>(`/api/catalog/types/${type.id}/reservations?status=free`).then(l => setList([...l].sort((a, b) => a.reservedFor.localeCompare(b.reservedFor) || a.year - b.year || a.number - b.number))).catch(e => toast(e.message, 'error')); }, []);
  const groups = useMemo(() => groupByDay(list || []), [list]);
  return (
    <Modal title="Folosește un număr rezervat" icon="clipboard" size="sm" onClose={onClose} footer={<><span className="grow" /><button className="btn" onClick={onClose}>Renunță</button></>}>
      {list == null ? <p className="muted">Se încarcă…</p> : list.length === 0 ? (
        <EmptyState icon="clipboard" title="Nu există numere rezervate libere" text="Rezervați numere din Registratură, cu butonul „Rezervă numere” de lângă „Înregistrează document”." />
      ) : (
        <div className="stack" style={{ gap: 14 }}>
          <p className="muted small">Alegeți numărul. Data înregistrării documentului este data pentru care a fost rezervat numărul.</p>
          {groups.map(g => (
            <div key={g.day}>
              <div className="section-title" style={{ marginTop: 0 }}>{dayLabel(g.day)}</div>
              <div className="res-pick">
                {g.items.map(r => <button key={r.id} className="res-num" onClick={() => onPick(r)} title={`Rezervat de ${r.reservedByName || '—'}`}>{r.full}</button>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
